import type { PlacementZone } from "../../../scene-canvas/scene-rules/placement-rules";
import type { DeviceType } from "../../../scene-canvas/shared/responsiveness";

type QuestionButtonPlacement = Omit<PlacementZone, "count"> & {
  w: number;
  h: number;
  rowAlign?: "start" | "center" | "end";
  colAlign?: "start" | "center" | "end";
};

function slot(
  verticalK: [number, number],
  horizontalK: [number, number],
  size: Pick<QuestionButtonPlacement, "w" | "h"> = { w: 4, h: 2 }
): QuestionButtonPlacement {
  return {
    verticalK,
    horizontalK,
    ...size,
    rowAlign: "center",
    colAlign: "center",
  };
}

const DEFAULT_BUTTON_SLOTS: Record<DeviceType, QuestionButtonPlacement[]> = {
  laptop: [
    slot([0.18, 0.22], [0.08, 0.14]),
    slot([0.18, 0.22], [0.74, 0.8]),
    slot([0.3, 0.34], [0.3, 0.36]),
    slot([0.3, 0.34], [0.58, 0.64]),
    slot([0.46, 0.52], [0.42, 0.48]),
    slot([0.58, 0.64], [0.1, 0.16]),
  ],
  tablet: [
    slot([0.244, 0.276], [0.181, 0.226]),
    slot([0.244, 0.276], [0.682, 0.728]),
    slot([0.34, 0.372], [0.348, 0.394]),
    slot([0.34, 0.372], [0.561, 0.606]),
    slot([0.468, 0.516], [0.439, 0.485]),
    slot([0.564, 0.612], [0.196, 0.242]),
  ],
  mobile: [
    slot([0.25, 0.278], [0.231, 0.27], { w: 3, h: 2 }),
    slot([0.25, 0.278], [0.654, 0.692], { w: 3, h: 2 }),
    slot([0.336, 0.365], [0.372, 0.41], { w: 3, h: 2 }),
    slot([0.336, 0.365], [0.551, 0.59], { w: 3, h: 2 }),
    slot([0.451, 0.494], [0.449, 0.487], { w: 3, h: 2 }),
    slot([0.538, 0.581], [0.244, 0.282], { w: 3, h: 2 }),
  ],
};

const QUESTION_BUTTON_SLOTS: Record<
  string,
  Record<DeviceType, QuestionButtonPlacement[]>
> = {
  q1: {
    laptop: [
      slot([0.75, 0.77], [0.1, 0.15]),
      slot([0.8, 0.85], [0.75, 0.8]),
      slot([0.66, 0.7], [0.3, 0.4]),
      slot([0.75, 0.75], [0.36, 0.88]),
      slot([0.84, 0.88], [0.35, 0.45]),
    ],
    tablet: [
      slot([0.65, 0.65], [0.15, 0.15]),
      slot([0.7, 0.7], [0.7, 0.7]),
      slot([0.9, 0.9], [0.1, 0.1]),
      slot([0.75, 0.75], [0.3, 0.3]),
      slot([0.85, 0.85], [0.6, 0.64]),
    ],
    mobile: [
      slot([0.82, 0.82], [0.1, 0.1], { w: 3, h: 2 }),
      slot([0.63, 0.63], [0.25, 0.25], { w: 3, h: 2 }),
      slot([0.88, 0.88], [0.75, 0.75], { w: 3, h: 2 }),
      slot([0.94, 0.94], [0.30, 0.30], { w: 3, h: 2 }),
      slot([0.74, 0.74], [0.65, 0.65], { w: 3, h: 2 }),
    ],
  },
  q2: {
    laptop: [
      slot([0.60, 0.64], [0.8, 0.84]),
      slot([0.66, 0.70], [0.1, 0.15]),
      slot([0.82, 0.87], [0.66, 0.72]),
      slot([0.77, 0.81], [0.24, 0.3]),
      slot([0.68, 0.72], [0.5, 0.54]),
      slot([0.84, 0.88], [0.36, 0.42]),
    ],
    tablet: [
      slot([0.62, 0.62], [0.56, 0.6]),
      slot([0.70, 0.70], [0.08, 0.08]),
      slot([0.76, 0.76], [0.7, 0.7]),
      slot([0.81, 0.81], [0.22, 0.22]),
      slot([0.85, 0.85], [0.6, 0.62]),
      slot([0.92, 0.92], [0.35, 0.35]),
    ],
    mobile: [
      slot([0.75, 0.75], [0.31, 0.42], { w: 3, h: 2 }),
      slot([0.82, 0.82], [0.75, 0.75], { w: 3, h: 2 }),
      slot([0.92, 0.92], [0.1, 0.15], { w: 3, h: 2 }),
      slot([0.84, 0.84], [0.2, 0.25], { w: 3, h: 2 }),
      slot([0.65, 0.65], [0.1, 0.1], { w: 3, h: 2 }),
      slot([0.7, 0.7], [0.8, 0.8], { w: 3, h: 2 }),
    ],
  },
  q3: {
    laptop: [
      slot([0.60, 0.64], [0.06, 0.12]),
      slot([0.65, 0.69], [0.58, 0.64]),
      slot([0.70, 0.74], [0.84, 0.90]),
      slot([0.75, 0.79], [0.30, 0.36]),
      slot([0.86, 0.92], [0.14, 0.20]),
      slot([0.84, 0.89], [0.70, 0.76]),
    ],
    tablet: [
      slot([0.62, 0.62], [0.14, 0.14]),
      slot([0.71, 0.71], [0.88, 0.88]),
      slot([0.77, 0.77], [0.06, 0.06]),
      slot([0.82, 0.82], [0.64, 0.64]),
      slot([0.87, 0.87], [0.28, 0.28]),
      slot([0.92, 0.92], [0.82, 0.82]),
    ],
    mobile: [
      slot([0.8, 0.8], [0.4, 0.45], { w: 3, h: 2 }),
      slot([0.85, 0.85], [0.05, 0.15], { w: 3, h: 2 }),
      slot([0.87, 0.87], [0.6, 0.6], { w: 3, h: 2 }),
      slot([0.93, 0.93], [0.25, 0.4], { w: 3, h: 2 }),
      slot([0.63, 0.63], [0.1, 0.2], { w: 3, h: 2 }),
      slot([0.72, 0.72], [0.82, 0.82], { w: 3, h: 2 }),
    ],
  },
  q4: {
    laptop: [
      slot([0.61, 0.65], [0.16, 0.22]),
      slot([0.67, 0.71], [0.80, 0.86]),
      slot([0.73, 0.77], [0.04, 0.10]),
      slot([0.78, 0.82], [0.50, 0.56]),
      slot([0.83, 0.88], [0.68, 0.74]),
      slot([0.88, 0.94], [0.30, 0.36]),
    ],
    tablet: [
      slot([0.62, 0.62], [0.50, 0.50]),
      slot([0.70, 0.70], [0.90, 0.90]),
      slot([0.76, 0.76], [0.10, 0.10]),
      slot([0.82, 0.82], [0.70, 0.70]),
      slot([0.87, 0.87], [0.26, 0.26]),
      slot([0.9, 0.9], [0.5, 0.5]),
    ],
    mobile: [
      slot([0.83, 0.83], [0.15, 0.15], { w: 3, h: 2 }),
      slot([0.8, 0.8], [0.7, 0.7], { w: 3, h: 2 }),
      slot([0.92, 0.92], [0.25, 0.25], { w: 3, h: 2 }),
      slot([0.88, 0.88], [0.5, 0.5], { w: 3, h: 2 }),
      slot([0.62, 0.62], [0.2, 0.22], { w: 3, h: 2 }),
      slot([0.71, 0.71], [0.78, 0.78], { w: 3, h: 2 }),
    ],
  },
  q5: {
    laptop: [
      slot([0.61, 0.65], [0.8, 0.84]),
      slot([0.68, 0.72], [0.34, 0.4]),
      slot([0.75, 0.79], [0.06, 0.12]),
      slot([0.82, 0.86], [0.60, 0.66]),
      slot([0.88, 0.94], [0.40, 0.46]),
    ],
    tablet: [
      slot([0.62, 0.62], [0.6, 0.6]),
      slot([0.9, 0.9], [0.4, 0.4]),
      slot([0.82, 0.82], [0.64, 0.66]),
      slot([0.86, 0.86], [0.1, 0.12]),
      slot([0.6, 0.7], [0.1, 0.15]),
    ],
    mobile: [
      slot([0.62, 0.65], [0.3, 0.4], { w: 3, h: 2 }),
      slot([0.75, 0.75], [0.7, 0.7], { w: 3, h: 2 }),
      slot([0.87, 0.87], [0.62, 0.62], { w: 3, h: 2 }),
      slot([0.92, 0.94], [0.2, 0.24], { w: 3, h: 2 }),
      slot([0.79, 0.79], [0.10, 0.10], { w: 3, h: 2 }),
    ],
  },
};

export function getQuestionButtonPlacement(
  questionId: string,
  optionIndex: number,
  device: DeviceType
): QuestionButtonPlacement {
  return (
    QUESTION_BUTTON_SLOTS[questionId][device][optionIndex] ??
    DEFAULT_BUTTON_SLOTS[device][optionIndex % DEFAULT_BUTTON_SLOTS[device].length]
  );
}
